// apps/client/src/ui/screens/invites.tsx (M0-T17) — the in-town Menu's invite links: create, list, copy, share, revoke. The mayor also
// chooses here who can make them (town setting residentsCanInvite, spec §4.3), until M1-T18's town settings screen.
// Reads the current town from the typed WorldSlice fields townId/town (revision 10 item 3).
import { tuning } from '@whiskerton/content';
import type { InviteView, TownId } from '@whiskerton/shared';
import { canInvite } from '@whiskerton/shared/rules/permissions';   // subpath-only (not in the barrel)
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/endpoints';
import { getGame } from '../../game/instance';
import { HttpError } from '../../api/http';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Button, Panel, Tabs } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { invites: undefined } }

const UNLIMITED = 'unlimited';
const fail = (e: unknown) => useGame.getState().toast(e instanceof HttpError ? (`errors.${e.error}` as const) : 'errors.server_error', undefined, 'warn');

function Row({ invite, townName, canRevoke, onRevoke }: { invite: InviteView; townName: string; canRevoke: boolean; onRevoke: () => void }) {
  const t = useT();
  const expired = invite.expiresAt <= Date.now();
  const usedUp = invite.maxUses !== null && invite.uses >= invite.maxUses;
  const live = !invite.revoked && !expired && !usedUp;
  const copy = async () => {
    try { await navigator.clipboard.writeText(invite.url); useGame.getState().toast('towns.copied'); } catch { fail(null); }
  };
  const share = async () => {
    try { await navigator.share({ text: t('towns.shareText', { town: townName }), url: invite.url }); } catch { /* dismissed */ }
  };
  return (
    <li data-invite-url={invite.url} style={{ display: 'grid', gap: 6, background: 'var(--cream-100)', borderRadius: 16, padding: '10px 14px', opacity: live ? 1 : 0.6 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <strong style={{ fontWeight: 900, letterSpacing: '0.06em', fontVariantNumeric: 'tabular-nums' }}>{invite.code}</strong>
        <span>{invite.maxUses === null ? t('towns.usesOpen', { uses: invite.uses }) : t('towns.usesOf', { uses: invite.uses, max: invite.maxUses })}</span>
        <span>{invite.revoked ? t('towns.revoked') : expired ? t('towns.expired') : new Date(invite.expiresAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
        {invite.createdByName && <small>{t('towns.createdBy', { name: invite.createdByName })}</small>}
      </div>
      {live && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="quiet" onClick={() => void copy()}>{t('towns.copy')}</Button>
          {typeof navigator.share === 'function' && <Button variant="quiet" onClick={() => void share()}>{t('towns.share')}</Button>}
          {canRevoke && <Button variant="quiet" onClick={onRevoke}>{t('towns.revoke')}</Button>}
        </div>
      )}
    </li>
  );
}

function Invites({ close }: { close: () => void }) {
  const t = useT();
  const townId = useGame((s) => s.townId);
  const town = useGame((s) => s.town);
  const entry = useGame((s) => s.me?.towns.find((x) => x.id === s.townId) ?? null);
  const myName = useGame((s) => s.me?.profile?.name ?? null);
  const [invites, setInvites] = useState<InviteView[] | null>(null);
  const [hours, setHours] = useState<number>(tuning.town.inviteDefaultHours);
  const [uses, setUses] = useState<string>(UNLIMITED);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (id: TownId) => {
    try { setInvites((await api.listInvites(id)).invites); } catch (e) { fail(e); setInvites([]); }
  }, []);
  useEffect(() => { if (townId) void load(townId); }, [townId, load]);

  const create = async () => {
    if (!townId) return;
    setBusy(true);
    try {
      const { invite } = await api.createInvite(townId, { expiresInHours: hours, maxUses: uses === UNLIMITED ? null : Number(uses) });
      setInvites((prev) => [invite, ...(prev ?? []).filter((x) => x.code !== invite.code)]);
      useGame.getState().toast('towns.inviteReady');                // the new link heads the list beside the form
    } catch (e) { fail(e); } finally { setBusy(false); }
  };
  const revoke = async (invite: InviteView) => {
    if (!townId) return;
    try {
      await api.revokeInvite(townId, invite.code);
      setInvites((prev) => (prev ?? []).map((x) => (x.code === invite.code ? { ...x, revoked: true } : x)));
    } catch (e) { fail(e); }
  };

  const isMayor = entry?.role === 'mayor';
  const everyone = town?.settings.residentsCanInvite ?? false;
  const setEveryone = async (v: boolean) => {
    if (v === everyone) return;
    const res = await getGame().cmd.act('town.settings', { patch: { residentsCanInvite: v } });   // a reject toasts by itself
    const cur = useGame.getState().town;
    if (res.ok && cur) useGame.setState({ town: { ...cur, settings: res.r.settings } });             // town.updated follows for everyone
  };
  // Two columns on a landscape phone (§8.7): making a link on the left, the links on the right, newest first, so a new link's row with
  // Copy and Share shows at once beside the button that made it.
  return (
    <Panel title={t('towns.invitesTitle')}>
      <div style={{ display: 'grid', gap: '14px 22px', width: 720, maxWidth: '100%', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 10 }}>
          <strong>{t('towns.expiresIn')}</strong>
          <Tabs tabs={tuning.town.inviteHourOptions.map((h) => ({ id: String(h), label: t(`towns.hours${h}`) }))} active={String(hours)}
            onChange={(id) => setHours(Number(id))} />
          <strong>{t('towns.maxUses')}</strong>
          <Tabs tabs={[{ id: UNLIMITED, label: t('towns.usesUnlimited') }, ...tuning.town.inviteUseOptions.map((n) => ({ id: String(n), label: String(n) }))]}
            active={uses} onChange={setUses} />
          <Button disabled={busy || !townId} onClick={() => void create()}>{t('towns.createInvite')}</Button>
          {isMayor && town && <>
            <strong>{t('towns.whoInvites')}</strong>
            <Tabs tabs={[{ id: 'mayor', label: t('towns.inviteMayorOnly') }, { id: 'everyone', label: t('towns.inviteEveryone') }]}
              active={everyone ? 'everyone' : 'mayor'} onChange={(id) => void setEveryone(id === 'everyone')} />
          </>}
          <Button variant="quiet" onClick={close}>{t('common.close')}</Button>
        </div>
        {invites === null ? <p style={{ margin: 0 }}>{t('common.loading')}</p>
          : invites.length === 0 ? <p style={{ margin: 0 }}>{t('towns.noInvites')}</p> : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
              {invites.map((inv) => (
                <Row key={inv.code} invite={inv} townName={town?.name ?? entry?.name ?? ''}
                  canRevoke={isMayor || (myName !== null && inv.createdByName === myName)} onRevoke={() => void revoke(inv)} />
              ))}
            </ul>
          )}
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'invites'> = {
  id: 'invites', layer: 'panel', component: Invites,
  menu: { label: 'towns.invitesMenu', order: 20, visible: (s) => {
    const entry = s.me?.towns.find((x) => x.id === s.townId);   // WorldSlice.townId / .town are typed from W2 (revision 10 item 3)
    return !!entry && !!s.town && canInvite(entry.role, s.town.settings);
  } },
};
